import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Paper,
} from '@mui/material';

const API_URL = '/api/users';
const USER_ROLES = ['ADMIN', 'USER_A', 'USER_B', 'SUPERADMIN'];

function UserForm() {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('');
  const [signatureFile, setSignatureFile] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    if (id) {
      setIsEditMode(true);
      setLoading(true);
      axios.get(`${API_URL}/${id}`)
        .then(response => {
          const user = response.data;
          setUsername(user.username);
          setEmail(user.email);
          setFullName(user.full_name || '');
          setRole(user.role);
          setLoading(false);
        })
        .catch(err => {
          setError('Failed to fetch user data.');
          setLoading(false);
        });
    }
  }, [id]);

  const handleFileChange = (e) => {
    setSignatureFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const userData = {
      username,
      email,
      full_name: fullName,
      role,
      password: password || undefined,
    };

    try {
      if (isEditMode) {
        await axios.put(`${API_URL}/${id}`, userData);
        if (signatureFile) {
          const formData = new FormData();
          formData.append('file', signatureFile);
          await axios.post(`${API_URL}/${id}/signature`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
        }
      } else {
        await axios.post(API_URL, userData);
      }
      navigate('/users');
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred.');
      setLoading(false);
    }
  };

  if (loading && isEditMode) return <CircularProgress />;

  return (
    <Container component={Paper} maxWidth="sm" sx={{ p: 4, mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        {isEditMode ? 'Edit User' : 'Create User'}
      </Typography>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <TextField
          margin="normal"
          required
          fullWidth
          id="username"
          label="Username"
          name="username"
          autoComplete="username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          disabled={isEditMode}
        />
        <TextField
          margin="normal"
          fullWidth
          id="email"
          label="Email Address"
          name="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <TextField
          margin="normal"
          fullWidth
          id="fullName"
          label="Full Name"
          name="fullName"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
        />
        <TextField
          margin="normal"
          required={!isEditMode}
          fullWidth
          name="password"
          label={isEditMode ? "New Password (leave blank to keep current)" : "Password"}
          type="password"
          id="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <FormControl fullWidth margin="normal" required>
          <InputLabel id="role-label">Role</InputLabel>
          <Select
            labelId="role-label"
            id="role"
            value={role}
            label="Role"
            onChange={(e) => setRole(e.target.value)}
          >
            {USER_ROLES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </Select>
        </FormControl>

        {isEditMode && (
          <FormControl fullWidth margin="normal">
            <Typography variant="subtitle1" gutterBottom>
              Upload Signature
            </Typography>
            <Button
              variant="contained"
              component="label"
            >
              Choose File
              <input
                type="file"
                hidden
                onChange={handleFileChange}
              />
            </Button>
            {signatureFile && <Typography variant="body2" sx={{ mt: 1 }}>{signatureFile.name}</Typography>}
          </FormControl>
        )}

        {error && (
          <Alert severity="error" sx={{ width: '100%', mt: 2 }}>
            {error}
          </Alert>
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : (isEditMode ? 'Save Changes' : 'Create User')}
        </Button>
      </Box>
    </Container>
  );
}

export default UserForm;